/**
 * Hook for sortable table functionality
 */
import { useState, useMemo } from 'react'

export type SortDirection = 'asc' | 'desc'

export interface SortConfig {
  key: string
  direction: SortDirection
}

export interface UseSortableTableReturn<T> {
  sortedData: T[]
  sortConfig: SortConfig | null
  requestSort: (key: string) => void
  getSortDirection: (key: string) => SortDirection | null
}

/**
 * Generic hook for making table data sortable
 *
 * @param data - Array of data to sort
 * @param defaultSort - Optional default sort configuration
 * @returns Sorted data and sort control functions
 *
 * @example
 * const { sortedData, sortConfig, requestSort, getSortDirection } = useSortableTable(
 *   holdings,
 *   { key: 'symbol', direction: 'asc' }
 * )
 */
export function useSortableTable<T>(
  data: T[] | undefined,
  defaultSort?: SortConfig
): UseSortableTableReturn<T> {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(defaultSort ?? null)

  const sortedData = useMemo(() => {
    if (!data) return []
    if (!sortConfig) return data

    return [...data].sort((a, b) => {
      // Access the value using the key as an index
      const aValue = (a as Record<string, unknown>)[sortConfig.key]
      const bValue = (b as Record<string, unknown>)[sortConfig.key]

      // Handle null/undefined - push to end
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      // Compare based on type
      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime()
      } else {
        // Fallback to string comparison
        comparison = String(aValue).localeCompare(String(bValue))
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison
    })
  }, [data, sortConfig])

  const requestSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        // Toggle direction if same key
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        }
      }
      // New key, default to ascending
      return { key, direction: 'asc' }
    })
  }

  const getSortDirection = (key: string): SortDirection | null => {
    if (sortConfig?.key === key) {
      return sortConfig.direction
    }
    return null
  }

  return {
    sortedData,
    sortConfig,
    requestSort,
    getSortDirection,
  }
}
